<template>
  <div class="h-full overflow-x-hidden">
    <div v-if="appointment" class="h-full">
      <div>
        <AppointmentStatusDropdown
          :appointment="appointment"
          @refresh-appointment="fetchAppointment"
        />
      </div>
      <div>
        <AppointmentServiceCategory
          :appointment="appointment"
          @refresh-appointment="fetchAppointment"
        />
        <NavTab :tabs="tabs" variant="default" @select="onTabSelect" />
      </div>
      <div class="py-4 px-8 bg-gray-50 -mx-4 h-full">
        <component
          :is="tabComponent"
          :appointment="appointment"
          @refresh-appointment="fetchAppointment"
          @request-save="onRequestSave"
        />
        <RescheduleNotifyModal
          v-if="pendingSave"
          :pending-payload="pendingSave.payload"
          :action-name="pendingSave.action"
          @hide-modal="onModalClose"
        />
      </div>
    </div>
    <div v-else class="h-full flex items-center justify-center">
      <Spinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable.js";
import { EventBus } from "@/lib/event_bus";
import { trackMobileWebEvent } from "@/lib/pendo";

import NavTab from "@/components/Seranova/NavTab/NavTab.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import VueChannel from "@/channels/VueChannel";
import RescheduleNotifyModal from "@/Scheduler/RescheduleNotifyModal.vue";
import { AppointmentV2 } from "@/typings/appointment";
import AppointmentDetailsTab from "./AppointmentDetailsTab.vue";
import AppointmentNotesTab from "./AppointmentNotesTab.vue";
import AppointmentServiceCategory from "./AppointmentServiceCategory.vue";
import AppointmentStatusDropdown from "./AppointmentStatusDropdown.vue";
const emit = defineEmits(["appointment-change"]);
const props = defineProps<{ appointmentId: string | number }>();
const appointment = ref<AppointmentV2 | null>(null);
const pendingSave = ref<{ action: string; payload: object } | null>(null);

const store = useStore();
const activeTab = ref("Details");

const tabs = computed(() => [
  { label: "Details", active: activeTab.value === "Details" },
  { label: "Notes", active: activeTab.value === "Notes" }
]);

const tabComponent = computed(() => {
  switch (activeTab.value) {
    case "Notes":
      return AppointmentNotesTab;
    default:
      return AppointmentDetailsTab;
  }
});

const onTabSelect = (tab: { label: string }) => {
  activeTab.value = tab.label;
};

let vueChannel: VueChannel | null = null;

const handleAppointmentUpdated = (data: { id: number }) => {
  if (String(data.id) === String(props.appointmentId)) {
    fetchAppointment();
  }
};

onMounted(() => {
  EventBus.$on("appointment-updated", handleAppointmentUpdated);

  vueChannel = new VueChannel();
  cable.subscribe(vueChannel).catch((err) => {
    console.log("Unable to subscribe to VueChannel", err);
  });
});

onBeforeUnmount(() => {
  EventBus.$off("appointment-updated", handleAppointmentUpdated);
  vueChannel?.disconnect();
});

const fetchAppointment = async () => {
  appointment.value = await store.dispatch("fetchAppointmentDetails", {
    id: props.appointmentId
  });
  emit("appointment-change", appointment.value);
};

// Refetch when the drawer is pointed at a different appointment. This is what
// lets the parent keep the drawer mounted across appointment switches (no
// :key remount), so opening/switching no longer churns the whole subtree.
watch(() => props.appointmentId, fetchAppointment, { immediate: true });

const onRequestSave = async (data: {
  action: string;
  payload: { skip_notifications?: boolean } & Record<string, unknown>;
}) => {
  const skipNotifications = data.payload.skip_notifications;

  // Tracked here rather than in either save path: this is the one point both
  // the backdate dispatch and the notify-prompt hand-off pass through, so the
  // edit is counted exactly once per submit.
  trackMobileWebEvent("appointment_edit_scheduled_time", {
    surface: "details_drawer",
    appointment_id: props.appointmentId,
    notify_prompted: skipNotifications === undefined,
    ...(skipNotifications !== undefined
      ? { notify_customer: !skipNotifications }
      : {})
  });

  // A backdate, or the mobile drawer's inline notify toggle, carries
  // skip_notifications and saves directly, bypassing the reschedule-notify
  // prompt that only fits a future-dated visit.
  if (data.payload.skip_notifications !== undefined) {
    try {
      await store.dispatch(data.action, data.payload);
      window.notify.success("Appointment updated");
    } catch {
      // The global axios interceptor already surfaces the server's specific
      // error (e.g. the scheduled-day floor); swallow so the refresh runs.
    } finally {
      fetchAppointment();
    }
    return;
  }
  pendingSave.value = data;
};

const onModalClose = () => {
  pendingSave.value = null;
  fetchAppointment();
};
</script>

<style></style>
